import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
  Breadcrumbs,
  Link,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Campaign as CampaignIcon,
  Send as SendIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useUserStore, useSubscriptionStore } from 'store';
import { getCsrfToken } from '../../utils';
import InfoIcon from '@mui/icons-material/Info';
import MessagePreviewDialog from '../shared/MessagePreviewDialog';

const TEMPLATE_VARIABLES = [
  { key: '{{customer_name}}', labelKey: 'campaign_var_customer_name' },
  { key: '{{vehicle_reg}}', labelKey: 'campaign_var_vehicle_reg' },
  { key: '{{tire_summary}}', labelKey: 'campaign_var_tire_summary' },
  { key: '{{tire_size}}', labelKey: 'campaign_var_tire_size' },
  { key: '{{tire_brand}}', labelKey: 'campaign_var_tire_brand' },
  { key: '{{tire_model}}', labelKey: 'campaign_var_tire_model' },
  { key: '{{tire_type}}', labelKey: 'campaign_var_tire_type' },
  { key: '{{dot_code}}', labelKey: 'campaign_var_dot_code' },
  { key: '{{workshop_name}}', labelKey: 'campaign_var_workshop_name' },
  { key: '{{workshop_phone}}', labelKey: 'campaign_var_workshop_phone' },
];

function SeasonalCampaignPage({ t }) {
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const { subscriptionInfo, fetchSubscriptionInfo, canUseSms } = useSubscriptionStore();
  const templateRef = useRef(null);

  // Check if SMS is available (not available during trial)
  const smsEnabled = canUseSms();
  const isTrial = subscriptionInfo?.is_trial;

  // Templates
  const [activeTemplate, setActiveTemplate] = useState('email'); // 'email' or 'sms'
  const [emailTemplate, setEmailTemplate] = useState('');
  const [smsTemplate, setSmsTemplate] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  // The number {{workshop_phone}} renders to: the tire hotel contact phone when set, else the company phone
  const [workshopPhone, setWorkshopPhone] = useState('');

  // Channel selection
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState('stored');
  const [onlyNeedsReplacement, setOnlyNeedsReplacement] = useState(false);
  const [filterType, setFilterType] = useState('');

  // Recipients
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);

  // Sending
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [recipientDialogOpen, setRecipientDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  // Live text preview, rendered by the backend (same code as the send) for the first recipient.
  const [previewText, setPreviewText] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Load default templates from org settings
  useEffect(() => {
    if (!subscriptionInfo) {
      fetchSubscriptionInfo();
    }
  }, []);

  useEffect(() => {
    if (!currentOrganization?.organization_id) return;

    const fetchOrgSettings = async () => {
      try {
        const response = await axios.get(
          `/api/organizations/${currentOrganization.organization_id}/`,
          { headers: { 'X-Organization-Id': currentOrganization.organization_id } }
        );
        const org = response.data;
        if (org.tire_hotel_campaign_email_template) {
          setEmailTemplate(org.tire_hotel_campaign_email_template);
        }
        if (org.tire_hotel_campaign_sms_template) {
          setSmsTemplate(org.tire_hotel_campaign_sms_template);
        }
        if (org.tire_hotel_campaign_email_subject) {
          setEmailSubject(org.tire_hotel_campaign_email_subject);
        }
        setWorkshopPhone((org.tire_hotel_contact_phone || '').trim() || org.phone || '');
      } catch (err) {
        console.error('Failed to load org settings:', err);
      }
    };

    fetchOrgSettings();
  }, [currentOrganization?.organization_id]);

  // Fetch matching cards for recipient count
  const fetchRecipients = useCallback(async () => {
    if (!currentOrganization?.organization_id) return;

    setLoadingCards(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (onlyNeedsReplacement) params.append('needs_replacement', 'true');
      if (filterType) params.append('tire_set_type', filterType);
      params.append('page_size', '1000'); // Fetch all for counting

      const response = await axios.get(
        `/api/tire-hotel-cards/?${params.toString()}`,
        { headers: { 'X-Organization-Id': currentOrganization.organization_id } }
      );
      const data = response.data.results || response.data;
      setCards(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch recipients:', err);
      setCards([]);
    } finally {
      setLoadingCards(false);
    }
  }, [currentOrganization?.organization_id, filterStatus, onlyNeedsReplacement, filterType]);

  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);

  // Group cards by customer for recipient list and count
  const recipientMap = {};
  cards.forEach((card) => {
    if (!card.customer) return;
    if (!recipientMap[card.customer]) {
      recipientMap[card.customer] = {
        customer_name: card.customer_name,
        customer_email: card.customer_email,
        customer_phone: card.customer_phone,
        cards: [],
      };
    }
    recipientMap[card.customer].cards.push(card);
  });
  const recipients = Object.values(recipientMap);
  const recipientCount = recipients.length;

  // Get the current template text
  const currentTemplate = activeTemplate === 'email' ? emailTemplate : smsTemplate;
  const setCurrentTemplate = activeTemplate === 'email' ? setEmailTemplate : setSmsTemplate;

  // Insert variable at cursor position
  const insertVariable = (variableKey) => {
    const textarea = templateRef.current?.querySelector('textarea');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = currentTemplate;
    const newText = text.substring(0, start) + variableKey + text.substring(end);
    setCurrentTemplate(newText);

    // Restore cursor position after React re-render
    requestAnimationFrame(() => {
      const newPos = start + variableKey.length;
      textarea.selectionStart = newPos;
      textarea.selectionEnd = newPos;
      textarea.focus();
    });
  };

  // The preview request: both templates as typed, rendered for the first recipient's card
  // (or the sample card when the filter matches nobody). Same endpoint as the settings tab.
  const loadPreview = useCallback(async () => {
    const res = await axios.post(
      `/api/organizations/${currentOrganization.organization_id}/message-preview/`,
      {
        kind: 'tire_hotel_campaign',
        draft: {
          tire_hotel_campaign_email_subject: emailSubject,
          tire_hotel_campaign_email_template: emailTemplate,
          tire_hotel_campaign_sms_template: smsTemplate,
          card_id: cards[0]?.id ?? null,
        },
      },
      { headers: { 'X-Organization-Id': currentOrganization.organization_id } },
    );
    return res.data;
  }, [currentOrganization?.organization_id, emailSubject, emailTemplate, smsTemplate, cards]);

  // Live text preview of the active template, debounced while typing.
  useEffect(() => {
    if (!currentOrganization?.organization_id) return undefined;
    if (!currentTemplate.trim()) {
      setPreviewText('');
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await loadPreview();
        if (cancelled) return;
        const variant = data?.variants?.[0];
        setPreviewText(activeTemplate === 'email' ? (variant?.email?.text || '') : (variant?.sms?.text || ''));
      } catch {
        if (!cancelled) setPreviewText('');
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [currentTemplate, activeTemplate, loadPreview, currentOrganization?.organization_id]);

  const handleSend = async () => {
    if (!sendEmail && !sendSms) return;

    // Each channel sends its own text; message_template stays the email text for the API.
    const messageTemplate = sendEmail ? emailTemplate : smsTemplate;
    if (!messageTemplate.trim()) return;

    setSending(true);
    try {
      await axios.post(
        '/api/tire-hotel-cards/send_campaign/',
        {
          message_template: messageTemplate,
          sms_template: smsTemplate || undefined,
          email_subject: emailSubject || undefined,
          send_email: sendEmail,
          send_sms: sendSms,
          filter_status: filterStatus || undefined,
          filter_needs_replacement: onlyNeedsReplacement || undefined,
          filter_tire_set_type: filterType || undefined,
        },
        {
          headers: {
            'X-CSRFToken': getCsrfToken(),
            'X-Organization-Id': currentOrganization.organization_id,
          },
        }
      );
      setSnackbar({
        open: true,
        message: t('tire_hotel.campaign_queued'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.message,
        severity: 'error',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box data-testid="seasonal-campaign-page">
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/tire-hotel')}
        >
          {t('tire_hotel.menu_title')}
        </Link>
        <Typography color="text.primary">{t('tire_hotel.campaigns')}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <CampaignIcon sx={{ fontSize: 28 }} />
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {t('tire_hotel.campaign_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('tire_hotel.campaign_subtitle')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Left Column: Template Editor */}
        <Box>
          <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {activeTemplate === 'email'
                ? t('tire_hotel.campaign_email_template')
                : t('tire_hotel.campaign_sms_template')}
            </Typography>

            {/* Template type toggle */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip
                label={t('tire_hotel.campaign_channel_email')}
                onClick={() => setActiveTemplate('email')}
                color={activeTemplate === 'email' ? 'primary' : 'default'}
                variant={activeTemplate === 'email' ? 'filled' : 'outlined'}
                icon={<EmailIcon />}
              />
              <Chip
                label={t('tire_hotel.campaign_channel_sms')}
                onClick={() => setActiveTemplate('sms')}
                color={activeTemplate === 'sms' ? 'primary' : 'default'}
                variant={activeTemplate === 'sms' ? 'filled' : 'outlined'}
                icon={<SmsIcon />}
              />
            </Box>

            {/* Variable insertion buttons */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {t('tire_hotel.campaign_insert_variable')}:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
              {TEMPLATE_VARIABLES.map((v) => (
                <Chip
                  key={v.key}
                  label={t(`tire_hotel.${v.labelKey}`)}
                  size="small"
                  variant="outlined"
                  onClick={() => insertVariable(v.key)}
                  sx={{ cursor: 'pointer' }}
                  data-testid={`campaign-var-${v.labelKey}`}
                />
              ))}
            </Box>

            {/* Email subject field (email tab only) */}
            {activeTemplate === 'email' && (
              <TextField
                fullWidth
                size="small"
                label={t('tire_hotel.campaign_email_subject')}
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder={t('tire_hotel.campaign_email_subject_placeholder')}
                helperText={`${currentOrganization?.organization_name || ''}: ${emailSubject || '...'}`}
                sx={{ mb: 2 }}
              />
            )}

            {/* Template textarea */}
            <TextField
              ref={templateRef}
              fullWidth
              multiline
              data-testid="campaign-template-field"
              rows={activeTemplate === 'email' ? 8 : 4}
              value={currentTemplate}
              onChange={(e) => setCurrentTemplate(e.target.value)}
              placeholder={activeTemplate === 'email'
                ? t('tire_hotel.campaign_email_template')
                : t('tire_hotel.campaign_sms_template')
              }
            />
          </Paper>

          {/* Filters & Channels */}
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('tire_hotel.campaign_filter_status')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t('tire_hotel.campaign_filter_status')}</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label={t('tire_hotel.campaign_filter_status')}
                >
                  <MenuItem value="">{t('tire_hotel.campaign_all_statuses')}</MenuItem>
                  <MenuItem value="stored">{t('tire_hotel.stored')}</MenuItem>
                  <MenuItem value="on_vehicle">{t('tire_hotel.on_vehicle')}</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={onlyNeedsReplacement}
                    onChange={(e) => setOnlyNeedsReplacement(e.target.checked)}
                    data-testid="campaign-only-needs-replacement"
                  />
                }
                label={t('tire_hotel.campaign_only_needs_replacement')}
              />

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t('tire_hotel.campaign_filter_type')}</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  label={t('tire_hotel.campaign_filter_type')}
                >
                  <MenuItem value="">{t('tire_hotel.campaign_all_types')}</MenuItem>
                  <MenuItem value="summer">{t('tire_hotel.summer')}</MenuItem>
                  <MenuItem value="winter">{t('tire_hotel.winter')}</MenuItem>
                  <MenuItem value="all_season">{t('tire_hotel.all_season')}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Channel selection */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" />
                    {t('tire_hotel.campaign_channel_email')}
                  </Box>
                }
              />
              <Tooltip
                title={!smsEnabled && isTrial ? t('SMS is not available during trial') : ''}
                placement="right"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendSms}
                      onChange={(e) => setSendSms(e.target.checked)}
                      disabled={!smsEnabled}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SmsIcon fontSize="small" color={smsEnabled ? 'inherit' : 'disabled'} />
                      <Typography sx={{ color: smsEnabled ? 'inherit' : 'text.disabled' }}>
                        {t('tire_hotel.campaign_channel_sms')}
                      </Typography>
                      {!smsEnabled && isTrial && (
                        <Tooltip title={t('SMS is not available during trial')}>
                          <InfoIcon sx={{ fontSize: 16, color: 'info.main', ml: 0.5 }} />
                        </Tooltip>
                      )}
                    </Box>
                  }
                />
              </Tooltip>
            </Box>
            {!smsEnabled && isTrial && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
                {t('SMS notifications are available with a paid subscription. Upgrade to send SMS messages to customers.')}
              </Alert>
            )}

            {/* Recipient count — clickable to open list */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }} data-testid="campaign-recipient-count">
              {loadingCards ? (
                <CircularProgress size={16} />
              ) : recipientCount > 0 ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setRecipientDialogOpen(true)}
                  sx={{ textTransform: 'none', p: 0 }}
                >
                  {t('tire_hotel.campaign_recipients', { count: recipientCount })} — {t('tire_hotel.campaign_show_list')}
                </Button>
              ) : (
                <Typography variant="body2" fontWeight={500}>
                  {t('tire_hotel.campaign_no_recipients')}
                </Typography>
              )}
            </Box>

            {/* Send button */}
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={() => setConfirmDialogOpen(true)}
              disabled={sending || recipientCount === 0 || (!sendEmail && !sendSms) || !currentTemplate.trim()}
              fullWidth
              data-testid="campaign-send-button"
            >
              {sending ? t('tire_hotel.campaign_sending') : t('tire_hotel.campaign_send')}
            </Button>
          </Paper>
        </Box>

        {/* Right Column: Preview */}
        <Box>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('tire_hotel.campaign_preview')}
            </Typography>

            {cards.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {t('tire_hotel.customer')}: {cards[0].customer_name}
              </Typography>
            )}

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                minHeight: 120,
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }}
              data-testid="campaign-preview"
            >
              {previewText || (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('tire_hotel.campaign_email_template')}...
                </Typography>
              )}
            </Paper>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => setPreviewDialogOpen(true)}
              disabled={!emailTemplate.trim() && !smsTemplate.trim()}
              sx={{ mt: 1.5 }}
              data-testid="campaign-open-preview-button"
            >
              {t('tire_hotel.campaign_preview_button')}
            </Button>
            <MessagePreviewDialog
              open={previewDialogOpen}
              onClose={() => setPreviewDialogOpen(false)}
              title={t('tire_hotel.campaign_preview_title')}
              loadPreview={loadPreview}
              showLanguage={false}
            />
          </Paper>
        </Box>
      </Box>

      {/* Recipient list dialog */}
      <Dialog
        open={recipientDialogOpen}
        onClose={() => setRecipientDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('tire_hotel.campaign_recipients', { count: recipientCount })}
          <IconButton size="small" onClick={() => setRecipientDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('tire_hotel.customer')}</TableCell>
                {sendEmail && <TableCell>{t('tire_hotel.campaign_channel_email')}</TableCell>}
                {sendSms && <TableCell>{t('tire_hotel.campaign_channel_sms')}</TableCell>}
                <TableCell>{t('tire_hotel.tire_info')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipients.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.customer_name}</TableCell>
                  {sendEmail && (
                    <TableCell sx={{ color: r.customer_email ? 'inherit' : 'error.main' }}>
                      {r.customer_email || '-'}
                    </TableCell>
                  )}
                  {sendSms && (
                    <TableCell sx={{ color: r.customer_phone ? 'inherit' : 'error.main' }}>
                      {r.customer_phone || '-'}
                    </TableCell>
                  )}
                  <TableCell>
                    {r.cards.map((c, j) => (
                      <Typography key={j} variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {[c.tire_brand, c.tire_size_display, t(`tire_hotel.${c.tire_set_type}`)].filter(Boolean).join(' · ')}
                      </Typography>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipientDialogOpen(false)}>{t('Close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Send confirmation dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>{t('tire_hotel.campaign_confirm_title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('tire_hotel.campaign_confirm_message', {
              count: recipientCount,
              channels: [sendEmail && t('tire_hotel.campaign_channel_email'), sendSms && t('tire_hotel.campaign_channel_sms')].filter(Boolean).join(' + '),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>{t('Cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => { setConfirmDialogOpen(false); handleSend(); }}
            startIcon={<SendIcon />}
          >
            {t('tire_hotel.campaign_send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default withTranslation()(SeasonalCampaignPage);
